<template>
  <div ref="chartEl" style="height: 300px"></div>
</template>

<script setup lang="ts">
import { useI18n } from "@/composables/useI18n";
import { formatCurrency } from "@/utils/tools";
import { PieChart } from "echarts/charts";
import {
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useTheme } from "vuetify";

echarts.use([
  PieChart,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  SVGRenderer,
  LegendComponent,
]);

const props = defineProps<{
  data: {
    value: number;
    name: string;
  }[];
  currencyCode: string;
}>();

const chartEl = ref<HTMLDivElement | null>(null);
const { locale } = useI18n();
const theme = useTheme();
const isDark = computed(() => theme.current.value.dark);

let chart: echarts.ECharts | null = null;
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  const el = chartEl.value;
  if (!el) return;
  chart = echarts.init(el);
  setOption(props.data);
  resizeObserver = new ResizeObserver(() => {
    chart?.resize();
  });
  resizeObserver.observe(el);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  chart?.dispose();
  chart = null;
});

watch(
  () => props.data,
  (value) => {
    setOption(value);
  },
);

watch([locale, isDark], () => {
  setOption(props.data);
});

type Data = {
  value: number;
  name: string;
};

const setOption = (data: Data[]) => {
  const total = data.reduce((acc, { value }) => acc + value, 0);
  const textColor = isDark.value ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)";

  const option = {
    color: [
      "#00C5D7",
      "#0070BA",
      "#268D96",
      "#00BA8C",
      "#00D670",
      "#67D1E8",
      "#84C0EF",
      "#3A80A4",
      "#2B5357",
    ],
    textStyle: { color: textColor },
    tooltip: {
      trigger: "item",
      valueFormatter: (value: number) => {
        const percent = total > 0 ? ((value / total) * 100).toFixed(2) : 0;
        const amount = formatCurrency({
          value,
          currencyCode: props.currencyCode,
          locale: locale.value,
        });
        return `${amount} (${percent}%)`;
      },
    },
    title: {
      text: formatCurrency({
        value: total,
        currencyCode: props.currencyCode,
        locale: locale.value,
      }),
      left: "center",
      top: "center",
      textStyle: { color: textColor },
    },
    legend: {
      type: "scroll",
      orient: "vertical",
      top: "center",
      right: "5%",
      textStyle: { color: textColor },
    },
    series: [
      {
        type: "pie",
        radius: ["60%", "90%"],
        data: [...data].sort((a, b) => b.value - a.value),
      },
    ],
  };

  chart?.setOption(option, { notMerge: true });
};
</script>
